/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	CosmosAsset,
	CosmosComponentContextProps,
	CosmosText,
	DocumentList,
	OptimizedCosmosAssetFragment,
} from "@cosmos/components";
import { useAuth } from "@cosmos/next-hooks";
import { TemplateProviders } from "@cosmos/next-templates";
import { PageHomeFragment } from "./DocumentListPage.page";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { DocumentListFragment } from "@cosmos/hygraph-schema";

interface CosmosPageProps {
	pageData?: PageHomeFragment;
	componentContext: CosmosComponentContextProps;
	brandedDocuments?: DocumentListFragment[];
}

export const LegalHubHomeClient = ({
	pageData,
	componentContext,
	brandedDocuments = [],
}: CosmosPageProps): React.JSX.Element => {
	useAuth(!componentContext.enableAuth);

	const icon: OptimizedCosmosAssetFragment = pageData?.homePage?.icon || { id: "", url: "", __typename: "Asset" };
	icon.width = 120;

	return (
		<TemplateProviders componentContext={componentContext}>
			<div
				className={css({
					maxWidth: "cosmos.article.sm",
					marginInline: "auto",
					paddingBlockStart: "eds.dimension.40",
					paddingBlockEnd: "eds.dimension.128",
					w: "full",
					mdDown: {
						paddingInline: "eds.dimension.24",
					},
				})}
			>
				<VStack gap="eds.dimension.80" alignItems="flex-start" w="full">
					<VStack gap="eds.dimension.32" alignItems="flex-start" w="full">
						{icon.url ? (
							<div className={css({ mdDown: { maxWidth: "eds.dimension.64" } })}>
								<CosmosAsset asset={icon} />
							</div>
						) : null}
						{pageData?.homePage?.title ? (
							<CosmosText
								variant="heading2XL"
								className={css({ marginBlockEnd: "eds.dimension.56", mdDown: { textStyle: "heading2XL" } })}
							>
								{pageData?.homePage?.title}
							</CosmosText>
						) : null}

						<VStack gap="eds.dimension.56" alignItems="flex-start" w="full">
							{brandedDocuments.length
								? brandedDocuments.map((brand) => {
										return <DocumentList key={brand?.heading} {...brand} />;
									})
								: null}
						</VStack>
					</VStack>
				</VStack>
			</div>
		</TemplateProviders>
	);
};
